import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Route, Routes, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { MatterCreatorBasic } from './basic';
import { MatterCreatorDetails } from './details';
import { MatterCreatorFiles } from './files';
import { BasicInfoForm, DetailsForm, basicInfoSchema, detailsSchema } from './schema';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGenerateMatterIssues } from '@/api/mutations/useGenerateMatterIssues';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Form } from '@/new/components/shadcn/form';

export function MatterCreator() {
  const { matterId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);

  const basicForm = useForm<BasicInfoForm>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      name: '',
      code: '',
      tokenLimit: undefined,
    },
  });

  const detailsForm = useForm<DetailsForm>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      issues: '',
      disputeType: '',
      userDefinedEntities: '',
      additionalContext: '',
      timePeriod: '',
      disputeStatus: '',
    },
  });

  // Pre-populate forms when matter data is loaded
  useEffect(() => {
    if (matter) {
      basicForm.reset({
        name: matter.name ?? '',
        code: matter.code ?? '',
        tokenLimit: matter.tokenLimit ?? undefined,
      });
      detailsForm.reset({
        issues: matter.issues ?? '',
        disputeType: matter.disputeType ?? '',
        userDefinedEntities: matter.userDefinedEntities ?? '',
        additionalContext: matter.additionalContext ?? '',
        timePeriod: matter.timePeriod ?? '',
        disputeStatus: matter.disputeStatus ?? '',
      });
    }
  }, [matter]);

  const { mutate: generateDetails, isLoading: isGeneratingDetails } = useGenerateMatterIssues({
    matterId: matterId ?? '',
  });

  const handleGenerateDetails = (file: File) => {
    trackCustomEvent(TrackingEventsV2.GenerateMatterDetails);
    generateDetails(
      { file },
      {
        onSuccess: (data) => {
          if (Object.values(data).every((value) => value === null)) {
            toast.error(
              `Couldn't extract any matter details from the document. Please try again with a different file.`,
            );
            return;
          }

          if (data.caseIssues) detailsForm.setValue('issues', data.caseIssues);
          if (data.caseType) detailsForm.setValue('disputeType', data.caseType);
          if (data.caseEntities) detailsForm.setValue('userDefinedEntities', data.caseEntities);

          toast.success('Matter details extracted successfully');
        },
        onError: () => {
          toast.error('Failed to extract matter details. Please try again.');
        },
      },
    );
  };

  if (!matterId) return null;

  return (
    <>
      <Breadcrumb
        items={[
          { href: '/matters', label: 'Matters', active: false },
          { href: `/matters/new/${matterId}`, label: 'New Matter', active: true },
        ]}
      />

      <Routes>
        <Route index element={<MatterCreatorFiles />} />
        <Route path="files" element={<MatterCreatorFiles />} />
        <Route
          path="basic"
          element={
            <Form {...basicForm}>
              <MatterCreatorBasic form={basicForm} />
            </Form>
          }
        />
        <Route
          path="details"
          element={
            <Form {...detailsForm}>
              <MatterCreatorDetails
                form={detailsForm}
                isGeneratingDetails={isGeneratingDetails}
                onGenerateDetails={handleGenerateDetails}
              />
            </Form>
          }
        />
      </Routes>
    </>
  );
}
